import {
    Component,
    computed,
    contentChild,
    Directive,
    effect,
    ElementRef,
    inject,
    input,
    NgModule,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import {
    AuiButtonStyleType,
    AuiColor,
    AuiSizeType,
    BORDER_CLASSES,
    COLORED_TEXT_CLASSES,
    DECENT_BACKGROUND_CLASSES,
    FOCUS_RING_CLASSES,
    FONT_SIZE_CLASSES,
    getAllClasses,
    PADDING_X_CLASSES,
    PADDING_Y_CLASSES,
    REVERSE_TEXT_CLASSES,
    ROUNDED_CLASSES,
    SOLID_BACKGROUND_CLASSES,
    SQUARE_WIDTH_CLASSES,
    TRANSPARENT_BACKGROUND_CLASSES,
} from '@ral/utils:core/ui';

import { injectAuiItemConfig } from '@ral/utils:core/ui';

@Directive({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[auiItemIcon]',
    standalone: false,
})
export class AuiItemIconComponent {
    position = input<'left' | 'right'>('left');
}
@Directive({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[auiItemLabel]',
    standalone: false,
})
export class AuiItemLabelComponent {}
@Directive({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[auiItemBadge]',
    standalone: false,
})
export class AuiItemBadgeComponent {}

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: '[auiItem]',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    template: `
        <ng-content select="[auiItemIcon]" />
        <ng-content select="[auiItemLabel]" />
        <ng-content select="[auiItemBadge]" />
    `,
    host: {
        '[disabled]': 'disabled()',
    },
})
export class AuiItemBaseComponent {
    private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private renderer = inject(Renderer2);
    private config = injectAuiItemConfig();

    fullWidth = input<boolean>(false);
    disabled = input<boolean, string | boolean>(false, {
        transform: (value) => {
            // Note: only these can be disabled
            const allowedDisabledTags = [
                'input',
                'select',
                'textarea',
                'fieldset',
                'optgroup',
                'option',
                'button',
            ].includes(this.elementRef.nativeElement.tagName.toLowerCase());
            if (!allowedDisabledTags && value) {
                console.warn(
                    `[AuiItemComponent] Tag ${this.elementRef.nativeElement.tagName} is not allowed to be disabled`
                );
            }
            return allowedDisabledTags && !!value;
        },
    });

    icon = contentChild(AuiItemIconComponent);
    label = contentChild(AuiItemLabelComponent, { read: ElementRef });
    badge = contentChild(AuiItemBadgeComponent, { read: ElementRef });
    iconEl = contentChild(AuiItemIconComponent, { read: ElementRef });

    size = input<AuiSizeType>();
    color = input<AuiColor>();
    auiStyle = input<AuiButtonStyleType>();
    rounded = input<'rounded-full' | 'rounded-none' | 'rounded' | 'no-border'>();

    computedSize = computed(() => {
        return this.size() ?? this.config.size();
    });
    computedColor = computed(() => {
        return this.color() ?? this.config.color();
    });
    computedStyle = computed(() => {
        return this.auiStyle() ?? this.config.auiStyle();
    });
    computedRounded = computed(() => {
        return this.rounded() ?? this.config.rounded();
    });
    /* if true, the item will be displayed as a block */

    /* do not use - this shoudl not be needed - please contact us if you need this */
    private labelUnder = false;
    /* do not use - this shoudl not be needed - please contact us if you need this */
    private fullHeight = false;

    render = effect(() => {
        const fullWidth = this.fullWidth();

        const size = this.computedSize();
        const color = this.computedColor();
        const style = this.computedStyle();
        const rounded = this.computedRounded();

        const label = this.label();
        const disabled = !!this.disabled();

        const iconElRef = this.iconEl();

        const parent = this.elementRef.nativeElement;
        const iconEl = iconElRef?.nativeElement;
        const pos = this.icon()?.position();
        if (label && iconEl) {
            if (pos === 'right') {
                // insertAfter: insert iconEl after labelEl
                if (label && label.nativeElement.nextSibling !== iconEl) {
                    parent.insertBefore(iconEl, label.nativeElement.nextSibling);
                } else {
                    parent.appendChild(iconEl);
                }
            } else {
                // insertBefore: insert iconEl before labelEl
                if (label) {
                    parent.insertBefore(iconEl, label.nativeElement);
                }
            }
        }

        let tailwindClasses = [
            'flex-nowrap',
            'justify-center',
            'gap-1.5',
            'text-center',
            'transition-all',
            'forced-color-adjust-none',
            'relative',
            'overflow-hidden',
            'items-center',
            'overflow-hidden',
        ];

        const disabledClasses = ['opacity-70', 'cursor-not-allowed'];
        const layoutClasses = ['flex', 'w-full', 'inline-flex', 'max-w-fit'];

        // Clear all classes
        this.elementRef.nativeElement.classList.remove(
            ...tailwindClasses,
            ...disabledClasses,
            ...layoutClasses,
            ...getAllClasses(
                BORDER_CLASSES,
                SOLID_BACKGROUND_CLASSES,
                TRANSPARENT_BACKGROUND_CLASSES,
                DECENT_BACKGROUND_CLASSES,
                REVERSE_TEXT_CLASSES,
                COLORED_TEXT_CLASSES,
                FOCUS_RING_CLASSES,
                SQUARE_WIDTH_CLASSES,
                ROUNDED_CLASSES,
                FONT_SIZE_CLASSES,
                PADDING_Y_CLASSES,
                PADDING_X_CLASSES
            )
        );

        // tailwindClasses.push(fullWidth ? 'block w-full' : 'inline-block');
        // tailwindClasses.push(labelUnder ? 'flex-col' : 'flex-row');
        if (fullWidth) {
            tailwindClasses.push(...['flex', 'w-full']);
        } else {
            tailwindClasses.push(...['inline-flex', 'max-w-fit']);
        }

        tailwindClasses.push(...FONT_SIZE_CLASSES[size]);
        tailwindClasses.push(...PADDING_Y_CLASSES[size]);
        // icon only or with label
        if (!label) {
            tailwindClasses.push(...SQUARE_WIDTH_CLASSES[size]);
        } else {
            tailwindClasses.push(...PADDING_X_CLASSES[size]);
        }

        if (rounded) {
            tailwindClasses.push(...ROUNDED_CLASSES[rounded]);
        }

        // colors

        if (style === 'solid') {
            if (rounded !== 'no-border') {
                tailwindClasses.push(...BORDER_CLASSES[color]);
            }
            tailwindClasses.push(...SOLID_BACKGROUND_CLASSES[color]);
            tailwindClasses.push(...REVERSE_TEXT_CLASSES[color]);
        }
        if (style === 'outline') {
            if (rounded !== 'no-border') {
                tailwindClasses.push(...BORDER_CLASSES[color]);
            }
            tailwindClasses.push(...SOLID_BACKGROUND_CLASSES['transparent']);
            tailwindClasses.push(...COLORED_TEXT_CLASSES[color]);
        }
        if (style === 'decent') {
            //const decentBackground = [...decentBackgrounds[color]];
            if (rounded !== 'no-border') {
                tailwindClasses.push(...BORDER_CLASSES['transparent']);
            }
            tailwindClasses.push(...DECENT_BACKGROUND_CLASSES[color]);
            tailwindClasses.push(...COLORED_TEXT_CLASSES[color]);
        }
        if (style === 'text') {
            if (rounded !== 'no-border') {
                tailwindClasses.push(...BORDER_CLASSES['transparent']);
            }
            tailwindClasses.push(...TRANSPARENT_BACKGROUND_CLASSES[color]);
            tailwindClasses.push(...COLORED_TEXT_CLASSES[color]);
        }

        // ring
        tailwindClasses.push(...FOCUS_RING_CLASSES[color]);

        if (
            disabled //|| loading
        ) {
            tailwindClasses = tailwindClasses.filter(
                (p) => !(p.includes('hover') || p.includes('active') || p.includes('focus'))
            );
            tailwindClasses.push(...disabledClasses);
        }

        tailwindClasses.forEach((cls) => {
            this.renderer.addClass(this.elementRef.nativeElement, cls);
        });
    });
}

@NgModule({
    declarations: [AuiItemBaseComponent, AuiItemIconComponent, AuiItemLabelComponent, AuiItemBadgeComponent],
    exports: [AuiItemBaseComponent, AuiItemIconComponent, AuiItemLabelComponent, AuiItemBadgeComponent],
})
export class AuiItemComponent {}
